import { boxFromPoints, dist, pt, type Box, type Pt } from './model/geom';
import {
  entityCrossesBox,
  entityGrips,
  entityHit,
  type CadEntity,
} from './model/entity';
import { DEFAULT_LAYER, newEntityId } from './model/document';
import { Scene } from './scene';
import { Viewport } from './render/viewport';
import { defaultSnapSettings, resolveSnap, type SnapResult, type SnapSettings } from './snap/snap';
import { parseCoordInput, distanceToPoint } from './input/coords';
import { findCommand } from './commands/registry';
import { touchedIds } from './ops/operation';
import { checkConflict, type PresenceHandle } from '../data/presence';
import type { CatalogBlock } from './blocks/catalog';
import { FALLBACK_CATALOG } from './blocks/standardLibrary';
import type { BlockCmdContext, PendingBlock } from './commands/block';
import type {
  Command,
  CommandRunner,
  InputToken,
  Prompt,
} from './commands/types';

/**
 * 편집기 컨트롤러 (작업지시서 P2-05 / P2-08 / P2-14 / P2-18)
 *
 * Scene(도면) · Viewport(화면) · 명령 엔진 · 선택 · 스냅을 하나로 묶고,
 * 마우스/키보드 이벤트를 명령이 이해하는 InputToken 으로 번역한다.
 *
 * React 는 이 객체를 상태로 두지 않는다. `subscribe()` 로 변경 통지만 받고
 * 필요한 값을 그때그때 읽어간다 — 도면 전체를 불변 복사하는 비용을 피하기 위함.
 */

export type DragMode = 'none' | 'select' | 'pan';

export interface EditorSnapshot {
  commandName: string | null;
  promptMessage: string;
  keywords: string[];
  selectionCount: number;
  cursor: Pt | null;
  snap: SnapResult | null;
  canUndo: boolean;
  canRedo: boolean;
  isDirty: boolean;
  entityCount: number;
}

export class Editor {
  scene: Scene;
  viewport = new Viewport();
  snapSettings: SnapSettings = defaultSnapSettings();

  selection = new Set<string>();
  currentLayer = DEFAULT_LAYER;

  /**
   * 사용 가능한 블록 목록. DB 를 못 읽어도 표준 60종으로 동작한다.
   * 물량 산출도 이 목록을 쓰므로, 도면에 넣을 수 있는 것과
   * 집계할 수 있는 것이 항상 같다.
   */
  catalog: CatalogBlock[] = FALLBACK_CATALOG;

  /**
   * 블록 라이브러리에서 고른 "지금 배치할 블록" (P4-05).
   * INSERT 명령은 명령행에서 블록명을 받지 않는다 — 60종 코드를 외우게
   * 하는 대신 패널에서 고르고 도면을 클릭하는 흐름으로 간다.
   */
  pendingBlock: PendingBlock | null = null;

  /**
   * 실시간 접속 핸들 (P4-13). 붙어 있으면 남이 잡은 객체 편집을 막는다.
   * 없으면(오프라인·미리보기) 검사 없이 진행한다 — 실시간이 안 된다고
   * 편집이 막히면 안 된다.
   */
  presence: PresenceHandle | null = null;

  /** 명령행 히스토리 (F2 패널) */
  log: string[] = [];

  /** 커서 월드 좌표 (스냅 적용 후) */
  cursor: Pt | null = null;
  snapResult: SnapResult | null = null;

  /** 고무줄 미리보기 도형 */
  preview: CadEntity[] = [];

  /** 드래그 상태 */
  dragMode: DragMode = 'none';
  dragStartScreen: { x: number; y: number } | null = null;
  dragStartWorld: Pt | null = null;
  dragCurrentWorld: Pt | null = null;

  /** 각도 고정 (`<45` 입력 시) */
  lockedAngle: number | null = null;

  /**
   * 그립 끌기 상태 (P2-12).
   * 명령을 치지 않고 객체의 점을 직접 잡아 옮기는 조작.
   */
  gripDrag: { entityId: string; gripIndex: number; before: CadEntity } | null = null;

  private runner: CommandRunner | null = null;
  private command: Command | null = null;
  private prompt: Prompt | null = null;
  private lastCommandName: string | null = null;

  /** selection 프롬프트에서 모으는 중인 집합 */
  private pickSet = new Set<string>();

  /** 명령이 만들어낸 마지막 확정점 — 상대좌표(@)의 기준 */
  private lastPoint: Pt | null = null;

  private listeners = new Set<() => void>();

  constructor(scene: Scene) {
    this.scene = scene;
  }

  /* ------------------------------------------------------------ 구독 */

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private notify(): void {
    for (const fn of this.listeners) fn();
  }

  snapshot(): EditorSnapshot {
    return {
      commandName: this.command?.name ?? null,
      promptMessage: this.promptMessage(),
      keywords: this.promptKeywords(),
      selectionCount: this.selection.size,
      cursor: this.cursor,
      snap: this.snapResult,
      canUndo: this.scene.canUndo,
      canRedo: this.scene.canRedo,
      isDirty: this.scene.isDirty,
      entityCount: this.scene.count,
    };
  }

  promptMessage(): string {
    if (this.prompt) return this.prompt.message;
    return '명령 입력';
  }

  private promptKeywords(): string[] {
    if (!this.prompt) return [];
    if (this.prompt.type === 'point' || this.prompt.type === 'distance') {
      return this.prompt.keywords ?? [];
    }
    if (this.prompt.type === 'keyword') return this.prompt.keywords;
    return [];
  }

  get isRunning(): boolean {
    return this.runner !== null;
  }

  get activePrompt(): Prompt | null {
    return this.prompt;
  }

  /**
   * 명령행에 한 줄 쓴다.
   *
   * ★ 반드시 notify() 까지 한다.
   *   예전에는 로그 배열에 넣기만 했다. 명령 실행 중에는 advance() 가 뒤이어
   *   notify() 를 불러 우연히 보였지만, 명령 밖에서 부르면 (가져오기 실패,
   *   DWG 안내 등) **메시지가 쌓이기만 하고 화면에 안 떴다.**
   *   사용자 입장에서는 버튼을 눌렀는데 아무 반응이 없는 것과 같다.
   */
  write(message: string): void {
    this.log.push(message);
    if (this.log.length > 300) this.log.shift();
    this.notify();
  }

  /* -------------------------------------------------------- 명령 실행 */

  startCommand(nameOrAlias: string): boolean {
    const cmd = findCommand(nameOrAlias);
    if (!cmd) {
      this.write(`알 수 없는 명령: ${nameOrAlias}`);
      this.notify();
      return false;
    }

    this.cancel(false);
    this.command = cmd;
    this.lastCommandName = cmd.name;
    this.write(`명령: ${cmd.name}`);

    this.runner = cmd.run({
      scene: this.scene,
      selection: this.selection,
      currentLayer: this.currentLayer,
      log: (m) => this.write(m),
      newId: newEntityId,
      pendingBlock: this.pendingBlock,
    } as BlockCmdContext);

    this.advance(undefined);
    return true;
  }

  /**
   * 배치할 블록을 고르고 INSERT 를 바로 시작한다.
   * 패널에서 클릭 → 도면 클릭 이 한 흐름이어야 매대를 줄줄이 놓을 수 있다.
   */
  pickBlock(block: CatalogBlock, params: Record<string, number> = {}, initialScale?: number): void {
    this.pendingBlock = { block, params, initialScale };
    this.startCommand('INSERT');
  }

  clearPendingBlock(): void {
    this.pendingBlock = null;
    this.notify();
  }

  /** 직전 명령 반복 — Enter / Space 의 기본 동작. CAD 사용자의 근육기억 1순위. */
  repeatLastCommand(): void {
    if (this.lastCommandName) this.startCommand(this.lastCommandName);
  }

  /** 명령에 토큰을 전달한다. */
  feed(token: InputToken): void {
    if (!this.runner) {
      // 명령이 없을 때의 Enter = 직전 명령 반복
      if (token.t === 'enter') this.repeatLastCommand();
      else if (token.t === 'esc') this.clearSelection();
      return;
    }

    if (token.t === 'esc') {
      this.cancel(true);
      return;
    }

    if (token.t === 'point') this.lastPoint = token.p;
    this.advance(token);
  }

  /** 제너레이터를 한 단계 진행시킨다. */
  private advance(token: InputToken | undefined): void {
    if (!this.runner) return;

    try {
      const step = token === undefined ? this.runner.next() : this.runner.next(token);

      if (step.done) {
        this.finish(step.value);
        return;
      }

      this.prompt = step.value;
      this.preview = [];

      // selection 프롬프트가 "이미 선택된 것"을 쓸 수 있으면 즉시 통과시킨다.
      // AutoCAD 의 명사→동사 순서(객체 먼저 고르고 명령)를 지원하는 지점.
      if (
        this.prompt.type === 'selection' &&
        this.prompt.useExisting &&
        this.selection.size > 0
      ) {
        const ids = [...this.selection];
        this.pickSet = new Set(ids);
        this.advance({ t: 'selection', ids });
        return;
      }

      if (this.prompt.type === 'selection') {
        this.pickSet = new Set();
      }
    } catch (err) {
      this.write(`명령 오류: ${err instanceof Error ? err.message : String(err)}`);
      this.cancel(false);
    }

    this.notify();
  }

  private finish(result: ReturnType<CommandRunner['next']>['value']): void {
    const name = this.command?.name ?? '';
    const ops = Array.isArray(result) ? result : [];

    if (ops.length > 0) {
      // 남이 편집 중인 객체를 건드리면 막는다 (P4-13).
      // 그냥 진행시키면 나중에 한쪽 작업이 통째로 덮인다.
      const touched = ops.flatMap(touchedIds);
      const conflict = this.presence ? checkConflict(touched, this.presence) : { ok: true as const };

      if (!conflict.ok) {
        this.write(`⚠️ ${conflict.message}`);
      } else {
        this.scene.commit(name, ops);
        this.write(`${name} 완료 — ${ops.length}개 연산`);
      }
    }

    this.runner = null;
    this.command = null;
    this.prompt = null;
    this.preview = [];
    this.pickSet.clear();
    this.lastPoint = null;
    this.lockedAngle = null;
    this.notify();
  }

  cancel(notify = true): void {
    if (this.runner) {
      this.runner.return(undefined);
      this.write(`${this.command?.name ?? ''} 취소됨`);
    }
    this.runner = null;
    this.command = null;
    this.prompt = null;
    this.preview = [];
    this.pickSet.clear();
    this.lastPoint = null;
    this.lockedAngle = null;
    if (notify) this.notify();
  }

  /* ---------------------------------------------------------- 명령행 */

  /**
   * 명령행에 입력한 문자열을 처리한다.
   * 명령이 실행 중이면 좌표/키워드로, 아니면 명령 이름으로 해석한다.
   */
  submitText(raw: string): void {
    const text = raw.trim();

    if (!this.runner) {
      if (text === '') this.repeatLastCommand();
      else this.startCommand(text);
      return;
    }

    const prompt = this.prompt;
    if (!prompt) return;

    if (text === '') {
      this.feed({ t: 'enter' });
      return;
    }

    // 자유 텍스트 프롬프트는 파싱하지 않는다 (문자 입력, 배율 등)
    if (prompt.type === 'text') {
      this.feed({ t: 'text', s: raw });
      return;
    }

    const parsed = parseCoordInput(text, {
      lastPoint: this.lastPoint ?? this.promptBase(),
      cursor: this.cursor,
      displayUnit: this.scene.doc.displayUnit,
      keywords: this.promptKeywords(),
    });

    switch (parsed.kind) {
      case 'point':
        this.feed({ t: 'point', p: parsed.point });
        break;

      case 'distance': {
        if (prompt.type === 'distance') {
          this.feed({ t: 'distance', v: parsed.value });
          break;
        }
        // 점을 요구하는데 거리만 들어왔다 → 동적 입력
        const base = this.promptBase() ?? this.lastPoint;
        if (!base) {
          this.write('기준점이 없어 거리만으로는 점을 정할 수 없습니다.');
          break;
        }
        const p = distanceToPoint(parsed.value, base, this.cursor, this.lockedAngle);
        if (p) this.feed({ t: 'point', p });
        else this.write('방향을 알 수 없습니다. 커서를 움직이거나 각도를 지정하세요.');
        break;
      }

      case 'angle':
        // 각도 고정 — 다음 거리 입력이 이 방향을 쓴다
        this.lockedAngle = parsed.radians;
        this.write(`각도 고정: ${((parsed.radians * 180) / Math.PI).toFixed(1)}°`);
        this.notify();
        break;

      case 'keyword':
        this.feed({ t: 'keyword', k: parsed.text });
        break;

      case 'empty':
        this.feed({ t: 'enter' });
        break;

      case 'invalid':
        this.write(`${parsed.text} — ${parsed.reason}`);
        this.notify();
        break;
    }
  }

  /** 현재 프롬프트의 기준점 */
  private promptBase(): Pt | null {
    if (!this.prompt) return null;
    if (this.prompt.type === 'point' || this.prompt.type === 'distance') {
      return this.prompt.base ?? null;
    }
    return null;
  }

  /* ------------------------------------------------------ 포인터 입력 */

  /** 화면 좌표를 받아 스냅을 적용한 월드 좌표로 바꾸고 미리보기를 갱신한다. */
  onPointerMove(sx: number, sy: number): void {
    const raw = this.viewport.toWorld(sx, sy);
    const base = this.promptBase() ?? this.lastPoint;

    this.snapResult = resolveSnap(this.scene, this.viewport, raw, base, this.snapSettings);
    this.cursor = this.snapResult.point;

    if (this.dragMode !== 'none') {
      this.dragCurrentWorld = this.cursor;
    }

    // 그립을 끄는 중이면 그 모양을 미리보기로 보여준다
    if (this.gripDrag) {
      const grips = entityGrips(this.gripDrag.before);
      const g = grips[this.gripDrag.gripIndex];
      this.preview = g ? [{ ...g.move(this.cursor), id: `preview-${this.gripDrag.entityId}` }] : [];
      this.notify();
      return;
    }

    // 고무줄 미리보기
    const p = this.prompt;
    if (p && (p.type === 'point' || p.type === 'distance') && p.preview) {
      this.preview = p.preview(this.cursor);
    } else if (this.preview.length > 0) {
      this.preview = [];
    }

    this.notify();
  }

  onPointerDown(sx: number, sy: number, button: number, shiftKey: boolean): void {
    // 가운데 버튼 = 화면 이동 (AutoCAD 와 동일)
    if (button === 1) {
      this.dragMode = 'pan';
      this.dragStartScreen = { x: sx, y: sy };
      return;
    }
    if (button !== 0) return;

    const world = this.snapResult?.point ?? this.viewport.toWorld(sx, sy);
    this.dragStartScreen = { x: sx, y: sy };
    this.dragStartWorld = world;
    this.dragCurrentWorld = world;

    const p = this.prompt;

    // 점을 요구하는 중이면 즉시 확정 (드래그 아님)
    if (p && (p.type === 'point' || p.type === 'distance')) {
      return; // 확정은 pointerUp 에서 — 드래그와 구분하기 위해
    }

    // 명령이 없고 선택된 객체의 그립을 눌렀다면 그립 끌기를 시작한다
    if (!this.runner) {
      const grip = this.findGripAt(sx, sy);
      if (grip) {
        this.gripDrag = grip;
        this.dragMode = 'none';
        return;
      }
    }

    // 선택 모드: 드래그면 상자 선택, 클릭이면 단일 선택
    this.dragMode = 'select';
    void shiftKey;
  }

  /** 화면 좌표 근처에 있는 (선택된 객체의) 그립을 찾는다. */
  private findGripAt(
    sx: number,
    sy: number
  ): { entityId: string; gripIndex: number; before: CadEntity } | null {
    if (this.selection.size === 0 || this.selection.size > 30) return null;

    const tolPx = 7;
    for (const id of this.selection) {
      const e = this.scene.get(id);
      if (!e || this.scene.isLocked(e)) continue;

      const grips = entityGrips(e);
      for (let i = 0; i < grips.length; i++) {
        const s = this.viewport.toScreen(grips[i]!.point);
        if (Math.hypot(s.x - sx, s.y - sy) <= tolPx) {
          return { entityId: id, gripIndex: i, before: e };
        }
      }
    }
    return null;
  }

  onPointerUp(sx: number, sy: number, button: number, shiftKey: boolean): void {
    if (button === 1) {
      this.dragMode = 'none';
      this.dragStartScreen = null;
      return;
    }
    if (button !== 0) return;

    const start = this.dragStartScreen;
    const movedPx = start ? Math.hypot(sx - start.x, sy - start.y) : 0;
    const isDrag = movedPx > 4;

    const world = this.snapResult?.point ?? this.viewport.toWorld(sx, sy);
    const p = this.prompt;

    // 그립 끌기 확정
    if (this.gripDrag) {
      const { entityId, gripIndex, before } = this.gripDrag;
      this.gripDrag = null;
      this.preview = [];

      if (isDrag) {
        const g = entityGrips(before)[gripIndex];
        if (g) {
          const after = g.move(world);
          this.scene.commit('GRIP', [
            { kind: 'update', command: 'GRIP', before: [before], after: [after] },
          ]);
          this.write(`그립 이동: ${entityId}`);
        }
      }
      this.resetDrag();
      this.notify();
      return;
    }

    // 점 확정
    if (p && (p.type === 'point' || p.type === 'distance')) {
      this.resetDrag();
      this.feed({ t: 'point', p: world });
      return;
    }

    // 선택
    if (isDrag && this.dragStartWorld) {
      const box = boxFromPoints(this.dragStartWorld, world);
      // 좌 → 우 = Window (완전 포함), 우 → 좌 = Crossing (걸치기만 해도)
      const crossing = world.x < this.dragStartWorld.x;
      const hits = this.selectInBox(box, crossing);
      this.applySelection(hits, shiftKey);
    } else {
      const hit = this.pickAt(world);
      this.applySelection(hit ? [hit] : [], shiftKey);
    }

    this.resetDrag();

    // selection 프롬프트 중이면 모으는 집합에 반영
    if (p?.type === 'selection') {
      this.pickSet = new Set(this.selection);
    }
    this.notify();
  }

  private resetDrag(): void {
    this.dragMode = 'none';
    this.dragStartScreen = null;
    this.dragStartWorld = null;
    this.dragCurrentWorld = null;
  }

  onWheel(sx: number, sy: number, deltaY: number): void {
    // 위로 굴리면 확대. AutoCAD 기본과 같다.
    const factor = deltaY < 0 ? 1.15 : 1 / 1.15;
    this.viewport.zoomAt(sx, sy, factor);
    this.notify();
  }

  panByPixels(dx: number, dy: number): void {
    this.viewport.panByPixels(dx, dy);
    this.notify();
  }

  /* ------------------------------------------------------------ 선택 */

  /** 클릭 지점의 엔티티. 위에 있는 것(나중에 그린 것)을 우선한다. */
  pickAt(world: Pt): string | null {
    const tol = this.viewport.pxToWorld(6);
    const box: Box = {
      minX: world.x - tol,
      minY: world.y - tol,
      maxX: world.x + tol,
      maxY: world.y + tol,
    };

    const candidates = this.scene
      .queryBox(box)
      .filter((e) => this.scene.isVisible(e) && !this.scene.isLocked(e));

    // 도면 배열의 뒤쪽 = 나중에 그린 것 = 위에 있는 것
    for (let i = candidates.length - 1; i >= 0; i--) {
      const e = candidates[i]!;
      if (entityHit(e, world, tol, this.scene.blocks)) return e.id;
    }
    return null;
  }

  /**
   * 상자 선택.
   * @param crossing true = 걸치기만 해도 선택(초록), false = 완전 포함만(파랑)
   */
  selectInBox(box: Box, crossing: boolean): string[] {
    const out: string[] = [];
    for (const e of this.scene.queryBox(box)) {
      if (!this.scene.isVisible(e) || this.scene.isLocked(e)) continue;

      if (crossing) {
        if (entityCrossesBox(e, box, this.scene.blocks)) out.push(e.id);
      } else {
        const b = this.scene.boxOf(e.id);
        if (b && b.minX >= box.minX && b.minY >= box.minY && b.maxX <= box.maxX && b.maxY <= box.maxY) {
          out.push(e.id);
        }
      }
    }
    return out;
  }

  private applySelection(ids: string[], additive: boolean): void {
    if (!additive) this.selection.clear();
    for (const id of ids) {
      if (additive && this.selection.has(id)) this.selection.delete(id);
      else this.selection.add(id);
    }
  }

  clearSelection(): void {
    if (this.selection.size === 0) return;
    this.selection.clear();
    this.notify();
  }

  selectAll(): void {
    this.selection.clear();
    for (const e of this.scene.entities) {
      if (this.scene.isVisible(e) && !this.scene.isLocked(e)) this.selection.add(e.id);
    }
    this.notify();
  }

  /** selection 프롬프트를 확정한다 (Enter). */
  confirmSelection(): void {
    if (this.prompt?.type !== 'selection') return;
    this.feed({ t: 'selection', ids: [...this.pickSet] });
  }

  /* ------------------------------------------------------------ 화면 */

  zoomExtents(): void {
    const ext = this.scene.extent();
    if (ext) this.viewport.fit(ext);
    else this.viewport.fit({ minX: 0, minY: 0, maxX: 10_000_000, maxY: 10_000_000 });
    this.notify();
  }

  /**
   * 이 객체 하나만 선택한다.
   * 댓글에서 "위치 보기" 를 눌렀을 때 쓴다 — 기존 선택은 지운다.
   */
  selectOnly(id: string): void {
    this.selection.clear();
    if (this.scene.get(id)) this.selection.add(id);
    this.notify();
  }

  /** 화면 중심을 이 좌표로 옮긴다. 배율은 그대로 둔다. */
  centerOn(p: Pt): void {
    this.viewport.center = { x: Math.round(p.x), y: Math.round(p.y) };
    this.notify();
  }

  zoomSelection(): void {
    if (this.selection.size === 0) return this.zoomExtents();
    let box: Box | null = null;
    for (const id of this.selection) {
      const b = this.scene.boxOf(id);
      if (!b) continue;
      box = box
        ? {
            minX: Math.min(box.minX, b.minX),
            minY: Math.min(box.minY, b.minY),
            maxX: Math.max(box.maxX, b.maxX),
            maxY: Math.max(box.maxY, b.maxY),
          }
        : { ...b };
    }
    if (box) this.viewport.fit(box);
    this.notify();
  }

  /* ------------------------------------------------------------ 토글 */

  toggleOrtho(): void {
    this.snapSettings.ortho = !this.snapSettings.ortho;
    if (this.snapSettings.ortho) this.snapSettings.polar = false;
    this.write(`직교 모드 ${this.snapSettings.ortho ? '켜짐' : '꺼짐'}`);
    this.notify();
  }

  toggleOsnap(): void {
    this.snapSettings.osnap = !this.snapSettings.osnap;
    this.write(`객체 스냅 ${this.snapSettings.osnap ? '켜짐' : '꺼짐'}`);
    this.notify();
  }

  toggleGridSnap(): void {
    this.snapSettings.gridSnap = !this.snapSettings.gridSnap;
    this.write(`격자 스냅 ${this.snapSettings.gridSnap ? '켜짐' : '꺼짐'}`);
    this.notify();
  }

  togglePolar(): void {
    this.snapSettings.polar = !this.snapSettings.polar;
    if (this.snapSettings.polar) this.snapSettings.ortho = false;
    this.write(`극좌표 추적 ${this.snapSettings.polar ? '켜짐' : '꺼짐'}`);
    this.notify();
  }

  /* ------------------------------------------------------ 실행 취소 */

  undo(): void {
    const name = this.scene.undo();
    if (name) this.write(`실행 취소: ${name}`);
    this.selection.clear();
    this.notify();
  }

  redo(): void {
    const name = this.scene.redo();
    if (name) this.write(`다시 실행: ${name}`);
    this.notify();
  }

  /**
   * 새로 등록한(또는 이번 세션에 DB 에서 막 읽어온) 블록을 카탈로그에 얹는다.
   * 같은 코드가 이미 있으면 건드리지 않는다 — 표준 60종을 덮어쓰면 안 된다.
   */
  addCustomBlock(block: CatalogBlock): void {
    if (this.catalog.some((b) => b.code === block.code)) return;
    this.catalog = [...this.catalog, block];
    this.notify();
  }

  /** 선택한 것 즉시 삭제 (Delete 키) */
  deleteSelection(): void {
    if (this.selection.size === 0) return;
    const targets: CadEntity[] = [];
    for (const id of this.selection) {
      const e = this.scene.get(id);
      if (e && !this.scene.isLocked(e)) targets.push(e);
    }
    if (targets.length === 0) return;

    this.scene.commit('ERASE', [{ kind: 'delete', command: 'ERASE', entities: targets }]);
    this.selection.clear();
    this.write(`${targets.length}개 삭제`);
    this.notify();
  }

  /** 현재 커서에서 가장 가까운 엔티티까지의 거리 (상태바 표시용) */
  cursorDistanceTo(id: string): number | null {
    if (!this.cursor) return null;
    const b = this.scene.boxOf(id);
    if (!b) return null;
    const cx = Math.max(b.minX, Math.min(this.cursor.x, b.maxX));
    const cy = Math.max(b.minY, Math.min(this.cursor.y, b.maxY));
    return dist(this.cursor, pt(cx, cy));
  }
}
